import React, { useEffect } from 'react';
import { Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaFolderOpen } from '@react-icons/all-files/fa/FaFolderOpen';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { AlertVariant, usePageAlert } from '../../../../commons/PageAlert';
import { TabPanelContainer, TabPanel } from '../../../../commons/TabPanel';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity } from '../../../../manager/categoria-actividad.reducer';
import {
  CATEGORIA_ACTIVIDAD_BASE,
  categoriaActividadConfigSeccionFromTab,
  categoriaActividadConfigTabFromSeccion,
  categoriaActividadConfigUrl,
  categoriaActividadListUrl,
  getCategoriaActividadListSearch,
  isCategoriaActividadConfigSeccion,
} from '../../../../shared/categoria-actividad';
import CategoriaActividadVencimientoTab from './categoria-actividad-vencimiento-tab';
import CategoriaActividadMailTab from './categoria-actividad-mail-tab';

const BACK = CATEGORIA_ACTIVIDAD_BASE;

export function CategoriaActividadConfigIndexRedirect() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  if (!id) return <Navigate to={BACK} replace />;
  return <Navigate to={categoriaActividadConfigUrl(id)} replace state={location.state} />;
}

export default function CategoriaActividadConfigPage() {
  const { id, seccion } = useParams<{ id: string; seccion: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const listSearch = getCategoriaActividadListSearch(location);
  const backToList = () => navigate({ pathname: BACK, search: listSearch });
  const activeTab = categoriaActividadConfigTabFromSeccion(seccion);

  const categoria = useAppSelector(s => s.categoriaActividad.entity) as any;
  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    if (id) dispatch(getEntity(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (!id || isCategoriaActividadConfigSeccion(seccion)) return;
    navigate(categoriaActividadConfigUrl(id), { replace: true, state: location.state });
  }, [id, seccion, navigate, location.state]);

  const handleTabChange = (tabId: string) => {
    if (!id) return;
    const nextSeccion = categoriaActividadConfigSeccionFromTab(tabId);
    if (nextSeccion === seccion) return;
    navigate(categoriaActividadConfigUrl(id, nextSeccion), { state: location.state });
  };

  const categoriaLabel = categoria?.descripcion ?? `#${id}`;
  const notify = (variant: AlertVariant, message: string) => showToast(variant, message);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Categoría de Actividad', to: categoriaActividadListUrl(listSearch), icon: <FaFolderOpen /> },
        { label: `Configuración — ${categoriaLabel}`, icon: <FaCog /> },
      ]} />

      <PageHeader
        icon={<FaCog />}
        iconColor="#64748b"
        title="Configuración de categoría"
        subtitle={`Mail y vencimiento para la categoría ${categoriaLabel}`}
      />

      {alert && <PageAlert alert={alert} />}

      {id && (
        <div style={s.tabCard}>
          <TabPanelContainer
            activeTab={activeTab}
            onTabChange={handleTabChange}
            accentColor="#f59e0b"
          >
            <TabPanel id="vencimiento" label="Vencimiento">
              <CategoriaActividadVencimientoTab
                categoriaActividadId={id}
                onBack={backToList}
                onNotify={notify}
              />
            </TabPanel>

            <TabPanel id="mail" label="Mail">
              <CategoriaActividadMailTab
                categoriaActividadId={id}
                onBack={backToList}
                onNotify={notify}
              />
            </TabPanel>
          </TabPanelContainer>
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  tabCard: {
    padding: '16px 20px 20px',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
  },
};
